import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useApolloClient } from "@apollo/client";
import ReportItemView from "./ReportItemView";
import ReportShareSheet from "@/features/reports/components/organisms/ReportShareSheet";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import { getScoutReportDisplayText } from "@/features/player-detail/utils/scoutingReportUtils";
import { useScoutReportTranslation } from "@/features/reports/hooks/useScoutReportTranslation";
import { generateScoutReportFilename } from "@/features/reports/utils/generateScoutReportFilename";
import { generateScoutReportPdf } from "@/features/reports/utils/generateScoutReportPdf";

import type { UiLocale } from "@/constants/locales";
import {
  isScoutReportAutoTranslateEnabled,
  resolveScoutReportAutoTranslate,
} from "@/features/reports/utils/scoutReportAutoTranslate";
import {
  PlayerDetailScoutReportsFragment,
  SupportedLocale,
  UserScoutReportRole,
  useReportsArchiveScoutReportMutation,
  useReportsGetScoutReportEditUrlLazyQuery,
  useReportsListScoutReportSharesQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import {
  formatMetadataDate,
  formatReportDisplayTime,
} from "@/utils/formatDateBasedOnDays";

const LOG_PREFIX = "[ScoutReport:ReportItem]";

/** Delay intervals (ms) for polling refetch after exiting edit mode */
const POST_EDIT_POLL_DELAYS = [0, 3000, 6000, 10000] as const;

/** Props for ReportItemContainer */
interface Props {
  item: PlayerDetailScoutReportsFragment;
  /** Player's display name for use in the PDF filename (SE-4257) */
  playerName?: string;
  /** When true, the report modal opens immediately on mount */
  defaultOpen?: boolean;
  /** Called when the report modal closes (after user dismisses it) */
  onClose?: () => void;
}

/**
 * Container component that manages scout report display logic.
 * Handles modal state, formats match information, and computes display values for the report.
 * When canEdit is true, fetches the server-generated Form.io edit URL and embeds it inline via iframe/WebView.
 * When canArchive is true, provides archive confirmation dialog and mutation handling.
 * Hides the Share button when the org has defaultScoutReportVisibility enabled,
 * since all members already have visibility into all reports.
 * Supports deep-linking via defaultOpen prop so the modal opens automatically when
 * navigated to from the player detail page with a reportId query parameter.
 * Provides on-demand translation via the useScoutReportTranslation hook, allowing
 * any user to translate the report text without edit permissions (SE-4245).
 * Provides PDF download on web via generateScoutReportPdf (SE-4256).
 * @param props - The component props
 * @param props.item - Scout report data including matches, score, date, and report text
 * @param props.playerName - Player's display name for the PDF filename (SE-4257)
 * @param props.defaultOpen - When true, the report modal opens immediately on mount
 * @param props.onClose - Called when the report modal closes
 */
const ReportItemContainer = ({
  item,
  playerName: playerNameForFilename,
  defaultOpen,
  onClose,
}: Props) => {
  /*
   * SE-6512: the container is the i18n subscriber for this pair. The view is
   * an arrow-shorthand expression (`component-structure/no-return-in-view`) and
   * so cannot call a hook; subscribing here means a locale change re-renders
   * the container and a fresh `t` flows down, which is what makes the report
   * chrome follow the language with no restart (R6).
   */
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { top } = useSafeAreaInsets();
  const client = useApolloClient();

  const currentUser = useCurrentUser();
  const isOwner = Boolean(item?.canEdit);
  const isEditor = Boolean(item?.canEdit);
  const canShare =
    !!currentUser &&
    !(currentUser?.organization?.defaultScoutReportVisibility ?? false);

  const [isModalOpen, setIsModalOpen] = useState(defaultOpen ?? false);

  // SE-6510. `useCurrentUser` is tri-state on purpose: `undefined` means this
  // session's identity has not arrived, `null` means signed out. Collapsing
  // those into "no preference" is exactly the bug the AC names — a viewer whose
  // `me` is still in flight would be read as an English one and the report
  // would open in English for good.
  const isViewerKnown = currentUser !== undefined;
  const autoTranslateDecision = useMemo(
    () =>
      resolveScoutReportAutoTranslate({
        isEnabled: isScoutReportAutoTranslateEnabled(),
        isViewerKnown,
        isSignedIn: !!currentUser,
        preference: currentUser?.languagePreference,
      }),
    [isViewerKnown, currentUser]
  );

  // The default is applied when the report is OPENED, never while it sits in
  // the list: a player page renders one ReportItem per report, so firing on
  // mount would cost one Bedrock invocation per row.
  const autoTranslateLocale =
    isModalOpen && autoTranslateDecision.kind === "translate"
      ? autoTranslateDecision.locale
      : null;

  const {
    selectedLocale,
    translate,
    isTranslating,
    failedLocale: translationFailedLocale,
    translatedText,
    resetToOriginal: onResetToOriginal,
    retry: onRetryTranslation,
  } = useScoutReportTranslation({
    reportId: item.reportId ?? "",
    autoTranslateLocale,
  });

  const [isLanguagePickerOpen, setIsLanguagePickerOpen] = useState(false);

  const onOpenLanguagePicker = useCallback(() => {
    setIsLanguagePickerOpen(true);
  }, []);

  const onCloseLanguagePicker = useCallback(() => {
    setIsLanguagePickerOpen(false);
  }, []);

  const onSelectLocale = useCallback(
    (locale: UiLocale) => {
      setIsLanguagePickerOpen(false);
      // The molecule speaks the offered-locale union; the mutation speaks the
      // generated enum. One cast, at the one boundary between them.
      void translate(locale as SupportedLocale);
    },
    [translate]
  );

  const { data: sharesData } = useReportsListScoutReportSharesQuery({
    variables: { scoutReportId: item.reportId ?? "" },
    skip: !isOwner || !item.reportId || !canShare,
  });

  const shareCount = useMemo(
    () =>
      canShare
        ? (sharesData?.listScoutReportShares?.filter(
            share => share.role === UserScoutReportRole.Viewer
          ).length ?? 0)
        : 0,
    [sharesData, canShare]
  );

  // Previous isModalOpen value, so report_opened fires only on the
  // closed -> open transition (covers defaultOpen; ignores reportId churn
  // while the modal stays open).
  const wasModalOpenRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [fetchEditUrl] = useReportsGetScoutReportEditUrlLazyQuery();
  const editRequestIdRef = useRef(0);
  const postEditPollTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const toast = useToastNotification();

  const [archiveScoutReport, { loading: isArchiving }] =
    useReportsArchiveScoutReportMutation({
      refetchQueries: ["PlayerDetailGetPlayer"],
      awaitRefetchQueries: true,
    });

  const cancelPostEditPolling = useCallback(() => {
    postEditPollTimersRef.current.forEach(timer => {
      clearTimeout(timer);
    });
    postEditPollTimersRef.current = [];
  }, []);

  const handlePress = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleOnModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditUrl(null);
    editRequestIdRef.current += 1;
    cancelPostEditPolling();
    // A manual language choice is scoped to this VIEW of the report (R3), so
    // closing discards it and reopening applies the viewer's default again.
    // The hook keeps its per-locale cache across the close, so that reopen is
    // free rather than a second provider call.
    onResetToOriginal();
    onClose?.();
  }, [onClose, cancelPostEditPolling, onResetToOriginal]);

  const handleEdit = useCallback(async () => {
    if (!item.reportId) return;

    console.info(LOG_PREFIX, "Edit requested for report:", item.reportId);
    const requestId = ++editRequestIdRef.current;
    setIsEditing(true);

    try {
      const result = await fetchEditUrl({
        variables: { scoutReportId: item.reportId },
      });
      const url = result.data?.getScoutReportEditUrl;
      if (url && requestId === editRequestIdRef.current) {
        console.info(LOG_PREFIX, "Edit URL resolved:", {
          reportId: item.reportId,
          urlLength: url.length,
          urlPreview: `${url.substring(0, 80)}...`,
        });
        setEditUrl(url);
      } else if (requestId === editRequestIdRef.current) {
        console.warn(
          LOG_PREFIX,
          "Edit URL returned empty — cannot open editor",
          { reportId: item.reportId }
        );
        setIsEditing(false);
      }
    } catch (error) {
      console.error(LOG_PREFIX, "Failed to fetch scout report edit URL:", {
        reportId: item.reportId,
        error: error instanceof Error ? error.message : error,
      });
      if (requestId === editRequestIdRef.current) {
        setIsEditing(false);
      }
    }
  }, [item.reportId, fetchEditUrl]);

  const handleEditClose = useCallback(() => {
    onResetToOriginal();
    setIsEditing(false);
    setEditUrl(null);
    cancelPostEditPolling();
    const timers = POST_EDIT_POLL_DELAYS.map(delay =>
      setTimeout(() => {
        void client
          .refetchQueries({ include: ["PlayerDetailGetPlayer"] })
          .catch(error => {
            console.warn(
              LOG_PREFIX,
              "Failed to refetch player data after edit",
              {
                reportId: item.reportId,
                error: error instanceof Error ? error.message : error,
              }
            );
          });
      }, delay)
    );
    postEditPollTimersRef.current = timers;
  }, [client, cancelPostEditPolling, item.reportId, onResetToOriginal]);

  const handleShare = useCallback(() => {
    setIsShareSheetOpen(true);
  }, []);

  const handleShareSheetClose = useCallback(() => {
    setIsShareSheetOpen(false);
  }, []);

  const handleArchivePress = useCallback(() => {
    setIsArchiveDialogOpen(true);
  }, []);

  const handleArchiveClose = useCallback(() => {
    setIsArchiveDialogOpen(false);
  }, []);

  const handleArchiveConfirm = useCallback(
    async (confirmation: string) => {
      if (!item.reportId) return;
      try {
        await archiveScoutReport({
          variables: { scoutReportId: item.reportId, confirmation },
        });
        toast.show({
          action: "success",
          description: "Scout report archived successfully",
        });
        setIsArchiveDialogOpen(false);
        handleOnModalClose();
      } catch (error) {
        toast.show({
          action: "danger",
          description:
            error instanceof Error
              ? error.message
              : "Failed to archive scout report",
        });
      }
    },
    [item.reportId, archiveScoutReport, toast, handleOnModalClose]
  );

  const teamText = getScoutReportDisplayText(item);
  const scoutName = item?.scoutName ?? "";
  const score = item?.overallScore ?? 0;
  const dateText = item?.reportDate
    ? formatReportDisplayTime(item.reportDate)
    : "";
  const reportText = translatedText ?? item?.qnaText ?? "";
  const createdAtFormatted = item?.reportDate
    ? formatMetadataDate(item.reportDate)
    : "";
  const editedByName = item?.lastEditorName ?? "";
  const editedAtFormatted = item?.lastEditDate
    ? formatMetadataDate(item.lastEditDate)
    : "";

  // Create matches array for display - use real matches if available, otherwise create from legacy data
  const displayMatches = useMemo(() => {
    if (item?.matches?.length) {
      return item.matches;
    }

    // Create pseudo-match from legacy data for display purposes
    if (item?.opponent && !/multi/i.test(item.opponent)) {
      return [
        {
          __typename: "MatchDetail" as const,
          team: item.team,
          opponent: item.opponent,
          ground: item.ground,
          matchDate: item.reportDate,
        },
      ];
    }

    return item?.matches || null;
  }, [item.matches, item.opponent, item.ground, item.team, item.reportDate]);

  const pdfFilename = useMemo(
    () =>
      generateScoutReportFilename({
        playerName: playerNameForFilename,
        matches: displayMatches?.map(m => ({
          opponent: m?.opponent ?? undefined,
          matchDate: m?.matchDate ?? undefined,
        })),
        reportDate: item.reportDate ?? undefined,
      }),
    [playerNameForFilename, displayMatches, item.reportDate]
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      await generateScoutReportPdf(
        {
          playerName: playerNameForFilename,
          opponent: item.opponent ?? undefined,
          team: item.team ?? undefined,
          ground: item.ground ?? undefined,
          matchDate: item.reportDate ?? undefined,
          scoutName: item.scoutName ?? undefined,
          reportDate: item.reportDate ?? undefined,
          reportText,
          overallScore: item.overallScore ?? undefined,
          matches: displayMatches?.map(m => ({
            team: m?.team ?? undefined,
            opponent: m?.opponent ?? undefined,
            ground: m?.ground ?? undefined,
            matchDate: m?.matchDate ?? undefined,
          })),
        },
        pdfFilename
      );
    } catch (error) {
      toast.show({
        action: "danger",
        description:
          error instanceof Error ? error.message : "Failed to generate PDF",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    playerNameForFilename,
    item,
    reportText,
    displayMatches,
    toast,
    pdfFilename,
  ]);

  // Download is a web-only capability (browser file download), but the
  // action row is responsive, so gate on width too (SE-5433): on small
  // screens the web build hides Download to match native mobile — where
  // it's absent — and to keep Translate/Share/Edit/Archive on one row.
  // Per .claude/rules/screen-size-not-device-type: capability gate
  // (Platform) combined with a width gate for the responsive surface.
  const isSmallScreen = useMobileScreen();
  const canDownloadReport = Platform.OS === "web" && !isSmallScreen;

  // SE-4010: opening the report detail is the feature's engagement signal.
  // Logged on the closed -> open transition (not the press handler) so the
  // `defaultOpen` path — where the parent mounts the item already open,
  // never invoking handlePress — is tracked too, while reportId churn on an
  // already-open modal never re-emits.
  useEffect(() => {
    const wasOpen = wasModalOpenRef.current;
    wasModalOpenRef.current = isModalOpen;
    if (!isModalOpen || wasOpen || !item.reportId) return;
    void AnalyticsTracker.scouting.logReportOpened({
      report_id: item.reportId,
    });
  }, [isModalOpen, item.reportId]);

  // A failed translation is deliberately NOT a toast any more (SE-6510 R4a).
  // A toast is transient, carries no retry, and the old one piped the
  // provider's raw error message straight to a scout. The notice is rendered
  // inline, next to the original text that is still on screen.

  // Cancel polling timers on unmount
  useEffect(() => cancelPostEditPolling, [cancelPostEditPolling]);

  return (
    <ReportItemView
      t={t}
      onPress={handlePress}
      teamText={teamText}
      dateText={dateText}
      scoutName={scoutName}
      score={score}
      isModalOpen={isModalOpen}
      reportText={reportText}
      onModalClose={handleOnModalClose}
      topSafeArea={top}
      matches={displayMatches}
      isDark={isDark}
      isOwner={isOwner}
      canEdit={item.canEdit}
      onEdit={handleEdit}
      onShare={canShare ? handleShare : undefined}
      isEditing={isEditing}
      editUrl={editUrl}
      onEditClose={handleEditClose}
      createdByName={scoutName}
      createdAtFormatted={createdAtFormatted}
      isEditor={isEditor}
      editedByName={editedByName}
      editedAtFormatted={editedAtFormatted}
      canArchive={item.canArchive}
      onArchive={handleArchivePress}
      isArchiveDialogOpen={isArchiveDialogOpen}
      onArchiveClose={handleArchiveClose}
      onArchiveConfirm={handleArchiveConfirm}
      isArchiving={isArchiving}
      shareCount={shareCount}
      onOpenLanguagePicker={onOpenLanguagePicker}
      isLanguagePickerOpen={isLanguagePickerOpen}
      onCloseLanguagePicker={onCloseLanguagePicker}
      onSelectLocale={onSelectLocale}
      isTranslating={isTranslating}
      selectedLocale={selectedLocale}
      hasTranslationFailure={translationFailedLocale !== null}
      isAwaitingTranslation={
        autoTranslateLocale !== null && isTranslating && translatedText === null
      }
      onRetryTranslation={onRetryTranslation}
      onResetToOriginal={onResetToOriginal}
      onDownload={canDownloadReport ? handleDownload : undefined}
      isDownloading={isDownloading}
    >
      <ReportShareSheet
        scoutReportId={item.reportId ?? ""}
        isOpen={isShareSheetOpen}
        onClose={handleShareSheetClose}
      />
    </ReportItemView>
  );
};

export default ReportItemContainer;
